import { useEffect, useState } from 'react';
import { LocalesKeyType } from 'types/types';
import { useRequestOtp } from 'hooks/mutations/use-request-otp.hook';
import { useVerifyOtp } from 'hooks/mutations/use-verify-otp.hook';
import { useSetUserProfile } from 'hooks/mutations/use-set-user-profile.hook';
import { MobileVerificationOtpResult } from 'types/data-processor.types';
import {
  FormHandlers,
  FormState,
  PersonalInformationData,
  PersonalInformationDataError,
} from '../types/onboarding-account-setup-types';
import {
  AU_SUBSCRIBER_LENGTH,
  extractSubscriberDigits,
  formatAuMobile,
  toAuNationalMobile,
  toE164AuMobile,
} from '../utils/onboarding-account-setup.format';
import {
  CONSENT_FLAGS,
  CONSENT_TIME_FLAGS,
} from '../onboarding-account-setup.constants';

export const useFormState = ({
  primaryMobileNumber = '',
}: {
  primaryMobileNumber?: string;
} = {}) => {
  const requestOtpMutation = useRequestOtp();
  const verifyOtpMutation = useVerifyOtp();
  const setUserProfileMutation = useSetUserProfile();

  // Mobile number state
  const [mobileNumber, setMobileNumber] = useState(() =>
    formatAuMobile(primaryMobileNumber),
  );
  const [mobileNumberError, setMobileNumberError] = useState<LocalesKeyType>();

  // primaryMobileNumber only changes when the underlying profile query
  // resolves with new data (initial load or a step's post-mutation refresh),
  // never while the user is typing, so re-syncing here can't clobber input.
  useEffect(() => {
    setMobileNumber(formatAuMobile(primaryMobileNumber));
  }, [primaryMobileNumber]);

  // Verification code state
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationCodeError, setVerificationCodeError] =
    useState<LocalesKeyType>();

  // Personal info state
  const [personalInformation, setPersonalInformation] =
    useState<PersonalInformationData>({
      firstName: '',
      middleName: '',
      lastName: '',
      dateOfBirth: '',
      mobileNumber,
    });
  const [personalInformationErrors, setPersonalInformationErrors] =
    useState<PersonalInformationDataError>({});

  // Form handlers
  const handleMobileNumberChange = (value: string) => {
    setMobileNumber(value);
    setMobileNumberError(undefined);
  };

  const handleVerificationCodeChange = (value: string) => {
    setVerificationCode(value);
    setVerificationCodeError(undefined);
  };

  const handlePersonalInformationChange = (
    field: keyof PersonalInformationData,
    value: string,
  ) => {
    setPersonalInformation((prev) => ({ ...prev, [field]: value }));
    setPersonalInformationErrors((prev) => ({ ...prev, [field]: null }));
  };

  // Validation handlers. Full AU mobile format validity is delegated to
  // the BE; the client only checks that the field is non-empty and has
  // the expected subscriber-digit count, so obviously incomplete numbers
  // (e.g. mid-typing) don't trigger an OTP request and burn rate limits.
  const validateMobileNumber = (): boolean => {
    if (!mobileNumber.trim()) {
      setMobileNumberError(
        'STR_ONBOARDING_ACCOUNT_SETUP_VALIDATION_MOBILE_REQUIRED',
      );
      return false;
    }
    if (extractSubscriberDigits(mobileNumber).length !== AU_SUBSCRIBER_LENGTH) {
      setMobileNumberError(
        'STR_ONBOARDING_ACCOUNT_SETUP_VALIDATION_MOBILE_INVALID',
      );
      return false;
    }
    return true;
  };

  const submitMobileNumber = async (): Promise<
    MobileVerificationOtpResult | undefined
  > => {
    if (requestOtpMutation.isPending) return undefined;

    setMobileNumberError(undefined);

    if (!validateMobileNumber()) return undefined;

    return requestOtpMutation.mutateAsync({
      mobileNumber: toE164AuMobile(mobileNumber),
    });
  };

  // Resend OTP without re-running mobile validation; the mobile number has
  // already passed validation to reach the verification step.
  const resendOtp = async (): Promise<
    MobileVerificationOtpResult | undefined
  > => {
    if (requestOtpMutation.isPending) return undefined;

    return requestOtpMutation.mutateAsync({
      mobileNumber: toE164AuMobile(mobileNumber),
    });
  };

  const validateVerificationCode = (): boolean => {
    if (!verificationCode.trim()) {
      setVerificationCodeError(
        'STR_ONBOARDING_ACCOUNT_SETUP_VALIDATION_CODE_INVALID',
      );
      return false;
    }
    return true;
  };

  const submitVerificationCode = async (): Promise<
    MobileVerificationOtpResult | undefined
  > => {
    if (verifyOtpMutation.isPending) return undefined;

    if (!validateVerificationCode()) return undefined;

    return verifyOtpMutation.mutateAsync({
      mobileNumber: toE164AuMobile(mobileNumber),
      otp: verificationCode,
    });
  };

  const validatePersonalInformation = (): boolean => {
    const errors: PersonalInformationDataError = {};
    if (!personalInformation.firstName.trim())
      errors.firstName =
        'STR_ONBOARDING_ACCOUNT_SETUP_VALIDATION_FIRST_NAME_REQUIRED';
    if (!personalInformation.lastName.trim())
      errors.lastName =
        'STR_ONBOARDING_ACCOUNT_SETUP_VALIDATION_LAST_NAME_REQUIRED';
    if (!personalInformation.dateOfBirth)
      errors.dateOfBirth =
        'STR_ONBOARDING_ACCOUNT_SETUP_VALIDATION_DOB_REQUIRED';

    if (Object.keys(errors).length > 0) {
      setPersonalInformationErrors(errors);
      return false;
    }
    return true;
  };

  // Submits the personal-information payload. Only invoked from the
  // myhealth-only review step, so the consent flag is hardcoded.
  const submitPersonalInformation = (): Promise<void> => {
    // The BE expects RFC3339 for `date_of_birth` (Go `*time.Time`); the
    // <input type="date"> yields `YYYY-MM-DD` so we pin it to UTC midnight to
    // avoid timezone drift shifting the calendar day.
    const dateOfBirthIso = personalInformation.dateOfBirth
      ? `${personalInformation.dateOfBirth}T00:00:00Z`
      : '';

    const consentTimeStamp = new Date().toISOString();

    return setUserProfileMutation
      .mutateAsync({
        body: {
          first_name: personalInformation.firstName,
          last_name: personalInformation.lastName,
          date_of_birth: dateOfBirthIso,
          custom_fields: [
            { name: CONSENT_FLAGS.myhealth, value: 'true' },
            {
              name: CONSENT_TIME_FLAGS.myhealth,
              value: consentTimeStamp,
            },
          ],
          phone_number: toAuNationalMobile(mobileNumber),
        },
      })
      .then(() => undefined);
  };

  // Bundled state and handlers
  const formState: FormState = {
    mobileNumber,
    mobileNumberError,
    verificationCode,
    verificationCodeError,
    personalInformation,
    personalInformationErrors,
  };

  const formHandlers: FormHandlers = {
    handleMobileNumberChange,
    handleVerificationCodeChange,
    handlePersonalInformationChange,
  };

  const requestOtpStatus = {
    isPending: requestOtpMutation.isPending,
  };

  const setUserProfileStatus = {
    isError: setUserProfileMutation.isError,
    isPending: setUserProfileMutation.isPending,
  };

  const verifyOtpStatus = {
    isPending: verifyOtpMutation.isPending,
  };

  return {
    formState,
    formHandlers,
    requestOtpStatus,
    setUserProfileStatus,
    validateMobileNumber,
    validateVerificationCode,
    validatePersonalInformation,
    submitMobileNumber,
    submitVerificationCode,
    submitPersonalInformation,
    resendOtp,
    verifyOtpStatus,
  };
};
